import { useAudio } from '../contexts/AudioPlayer'
import type { Pack } from '../lib/types'

function fmt(t: number): string {
  if (!isFinite(t) || t < 0) return '0:00'
  const m = Math.floor(t / 60)
  const s = Math.floor(t % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

// Deterministic pseudo-waveform bar heights (0.18–1.0) seeded from the slug so
// SSR and client render identically and each pack looks distinct.
function waveHeights(seed: string, n: number): number[] {
  let h = 2166136261
  for (let i = 0; i < seed.length; i++) h = (Math.imul(h ^ seed.charCodeAt(i), 16777619) >>> 0)
  const out: number[] = []
  for (let i = 0; i < n; i++) {
    h = (Math.imul(h, 1103515245) + 12345) >>> 0
    const a = (h % 1000) / 1000
    h = (Math.imul(h, 1103515245) + 12345) >>> 0
    const b = (h % 1000) / 1000
    out.push(0.18 + ((a + b) / 2) * 0.82)
  }
  return out
}

const NBARS = 56

export default function PreviewPlayer({ pack }: { pack: Pack }) {
  const { play, toggle, seek, currentTime, duration, isCurrent, isActive } = useAudio()

  if (!pack.audio_url) return null

  const loaded = isCurrent(pack.id)
  const playing = isActive(pack.id)
  const dur = loaded ? duration : 0
  const time = loaded ? currentTime : 0
  const frac = loaded && dur ? time / dur : 0
  const heights = waveHeights(pack.slug, NBARS)

  function start() {
    play({
      id: pack.id,
      title: pack.name,
      subtitle: pack.manufacturer ?? 'Forma Sound',
      src: pack.audio_url!,
    })
  }

  return (
    <div className="border border-line bg-ink-soft/40 p-4 sm:p-5 flex items-center gap-4 sm:gap-5">
      <button
        type="button"
        onClick={loaded ? toggle : start}
        aria-label={playing ? `Pause ${pack.name} preview` : `Play ${pack.name} preview`}
        className="shrink-0 size-12 rounded-full bg-paper text-ink grid place-items-center hover:scale-105 transition"
      >
        {playing ? (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M4 2.5h3v11H4zM9 2.5h3v11H9z" /></svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M4 2.5v11l9-5.5-9-5.5Z" /></svg>
        )}
      </button>

      <div className="flex-1 min-w-0">
        <div className="mono-label !text-[10px] mb-2">Audio preview</div>
        {/* waveform + seek */}
        <div className="relative h-10 group">
          <div className="absolute inset-0 flex items-center gap-[2px]" aria-hidden>
            {heights.map((h, i) => {
              const lit = i / NBARS <= frac
              return (
                <span
                  key={i}
                  style={{ height: `${Math.round(h * 100)}%` }}
                  className={`flex-1 rounded-full transition-colors ${
                    lit ? 'bg-paper' : 'bg-line-2 group-hover:bg-line-2/80'
                  }`}
                />
              )
            })}
          </div>
          <input
            type="range"
            min={0}
            max={dur || 0}
            step="any"
            value={time}
            onChange={(e) => {
              if (loaded) seek(Number(e.target.value))
              else start()
            }}
            onMouseDown={() => { if (!loaded) start() }}
            aria-label="Seek preview"
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
        </div>
      </div>

      <div className="shrink-0 mono-label !text-[10px] tabular-nums whitespace-nowrap">
        {fmt(time)} / {fmt(dur)}
      </div>
    </div>
  )
}
